/// <amd-module name="ExtensionManagement/ManagementHub/ExtensionsTable" />

import React = require("react");
import { format } from "VSS/Core/Util/String";
import { PersonaDetailsProvider } from "VSS/Features/Identities/PersonaDetailsProvider";
import { IVssPageContext } from "VSS/Platform/Context";
import { IListItemDetails, ListItem, List } from "VSSUI/List";
import { Ago } from "VSSUI/Ago";
import { IPersonaProvider, Persona, PersonaSize } from "VSSUI/Persona";
import { Pill, PillVariant } from "VSSUI/Pill";
import { PillGroup } from "VSSUI/PillGroup";
import { ITableColumn, ITableRow, SimpleTableCell, Table, TwoLineTableCell } from "VSSUI/Table";
import { css } from "VSSUI/Util";
import { AgoFormat } from "VSSUI/Utilities/Date";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { PublisherNameComponent } from "./PublisherNameComponent";
import { IExtensionActionViewModel, IExtensionViewModel, IInstalledExtensionViewModel, IRequestedExtensionViewModel, TabType } from "./UIContracts";
import * as Resources from "ExtensionManagement/ManagementHub/Resources.ExtManagmentHub";
import { Tooltip } from "VSSUI/TooltipEx";

/**
 * Extensions table props
 */
interface IExtensionsTableProps {
    /**
     * Extensions to display in table
     */
    extensions: IExtensionViewModel[];

    /**
     * Action to perform when user clicks on an row
     */
    OnExtensionSelection: (selectedExtension: IExtensionViewModel, allExtensions: IExtensionViewModel[]) => void;

    /**
     * Context for Persona provider.
     */
    pageContext: IVssPageContext;

    /**
     * Table type to be shown
     */
    tableId: string;
}

/**
 * Table that displays extensions
 */
export class ExtensionsTable extends React.Component<IExtensionsTableProps> {
    private requestedExtensionColumns: ITableColumn<IExtensionViewModel>[];
    private personaProvider: IPersonaProvider;

    constructor(props: IExtensionsTableProps) {
        super(props);
        this.requestedExtensionColumns = [
            {
                id: "displayNameDescription",
                name: Resources.Extension,
                width: -100,
                minWidth: 190,
                renderCell: this.renderExtensionNameColumn
            },
            {
                id: "requesterName",
                name: Resources.RequestedBy,
                width: 300,
                renderCell: this.renderRequesterNameColumn
            },
            {
                id: "resolverName",
                name: Resources.ResolvedBy,
                width: 300,
                renderCell: this.renderResolverNameColumn
            }
        ];
        this.personaProvider = new PersonaDetailsProvider(this.props.pageContext);
    }

    public render(): JSX.Element {
        if (this.props.tableId === TabType.requested) {
            if (!this.props.extensions.length) {
                return <span className="no-extensions-message">{Resources.NoRequestedExtensions}</span>;
            }
            return (
                <Table<IExtensionViewModel>
                    className="extensions-table flex-noshrink"
                    columns={this.requestedExtensionColumns}
                    itemProvider={new ArrayItemProvider<IExtensionViewModel>(this.props.extensions)}
                    onActivate={this.onActivate}
                />
            );
        } else {
            let rows: (rowIndex: number, item: IExtensionViewModel, details: IListItemDetails<IExtensionViewModel>) => JSX.Element | null;

            if (!this.props.tableId) {
                if (this.props.extensions.length === 0) {
                    return <span className="no-extensions-message">{Resources.NoInstalledExtensions}</span>;
                }
                rows = this.renderInstalledExtensionNameRow;
            } else {
                if (!this.props.extensions.length) {
                    return <span className="no-extensions-message">{Resources.NoSharedExtensions}</span>;
                }
                rows = this.renderSharedExtensionNameRow;
            }

            return (
                <List<IExtensionViewModel>
                    className="extensions-table flex-noshrink bolt-table-show-lines bolt-table padding-left-8"
                    renderRow={rows}
                    itemProvider={new ArrayItemProvider<IExtensionViewModel>(this.props.extensions)}
                    onActivate={this.onActivate}
                    role="list"
                    singleClickActivation={true}
                    width={"100%"}
                    minWidth={"190"}
                />
            );
        }
    }

    private onActivate = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<IExtensionViewModel>): void => {
        this.props.OnExtensionSelection(tableRow.data, this.props.extensions);
    };

    private renderInstalledExtensionNameRow = (
        rowIndex: number,
        listItem: IInstalledExtensionViewModel,
        details: IListItemDetails<IExtensionViewModel>,
        key?: string
    ): JSX.Element => { 
        let pills: JSX.Element | JSX.Element[] = [];

        if (listItem.reauthorizationRequired) {
            pills.push(
                <Pill variant={PillVariant.colored} color={{ red: 255, green: 160, blue: 122 }} key="action-required">
                    {Resources.ActionRequired}
                </Pill>
            );
        }

        if (listItem.extensionDisabled.value) {
            pills.push(<Pill key="disabled">{Resources.Disabled}</Pill>);
        }

        if (pills.length > 1) {
            pills = <PillGroup>{pills}</PillGroup>;
        }

        return this.renderExtensionRow(rowIndex, details, listItem.extensionDisabled.value, listItem, pills, key);
    };

    private renderSharedExtensionNameRow = (
        rowIndex: number,
        listItem: IExtensionViewModel,
        details: IListItemDetails<IExtensionViewModel>,
        key?: string
    ): JSX.Element => {

        return this.renderExtensionRow(rowIndex, details, false, listItem, [], key);
    };

    private renderExtensionRow = (
        index: number,
        details: IListItemDetails<IExtensionViewModel>,
        extensionDisabledValue: boolean,
        listItem: IExtensionViewModel,
        pills: JSX.Element | JSX.Element[] = [],
        key?: string
    ): JSX.Element => {
        return (
            <ListItem key={key || "list-item" + index} index={index} details={details}>
                <div className="flex-row h-scroll-hidden bolt-table-cell-content">
                <img alt="" className={css("extension-icon", extensionDisabledValue && "disabled-icon", "bolt-table-two-line-cell-icon")} src={listItem.extensionIconUrl} />
                    <div className="flex-column h-scroll-hidden">
                        <div className="flex-row">
                            <Tooltip text={listItem.extensionName}> 
                                <span className="extension-name primary-text word-break">{listItem.extensionName}</span>
                            </Tooltip>
                            <PublisherNameComponent
                                className="publisher-name secondary-text"
                                extension={listItem}
                                publisherNameText={format(Resources.by, listItem.publisherName)}
                            />
                            {pills}
                        </div>
                        <span className="description-row secondary-text">{listItem.shortDescription}</span>
                    </div>
                </div>
            </ListItem>
        );
    };

    /**
     * Function that renders a cell that displays a Extension's name and desciption in two lines
     * @param rowIndex
     * @param columnIndex
     * @param tableColumn
     * @param tableItem
     */
    private renderExtensionNameColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IExtensionViewModel>,
        tableItem: IExtensionViewModel
    ): JSX.Element => {
        return (
            <TwoLineTableCell
                key={"col-" + columnIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                iconProps={{
                    render: (className: string) => {
                        return <img alt="" className={css(className, "extension-icon")} src={tableItem.extensionIconUrl} />;
                    }
                }}
                line1={
                    <>
                        <Tooltip text={tableItem.extensionName}>
                            <span className="extension-name primary-text word-break">{tableItem.extensionName}</span>
                        </Tooltip>
                        <PublisherNameComponent
                            className="publisher-name secondary-text"
                            extension={tableItem}
                            publisherNameText={format(Resources.by, tableItem.publisherName)}
                        />
                    </>
                }
                line2={<div className="description-row">{tableItem.shortDescription}</div>}
            />
        );
    };

    private renderRequesterNameColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IRequestedExtensionViewModel>,
        tableItem: IRequestedExtensionViewModel
    ): JSX.Element => {
        return this.renderUserColumn(columnIndex, tableColumn, tableItem.requests[0]);
    };

    private renderResolverNameColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IRequestedExtensionViewModel>,
        tableItem: IRequestedExtensionViewModel
    ): JSX.Element => {
        if (tableItem.resolution) {
            return this.renderUserColumn(columnIndex, tableColumn, tableItem.resolution);
        }

        return <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex} />;
    };

    private renderUserColumn = (
        columnIndex: number,
        tableColumn: ITableColumn<IRequestedExtensionViewModel>,
        action: IExtensionActionViewModel
    ): JSX.Element => {
        return (
            <TwoLineTableCell
                key={"col-" + columnIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                iconProps={
                    action.userName
                        ? {
                              render: (className: string) => {
                                  const identity = {
                                      displayName: action.userName,
                                      id: action.userId,
                                      imageUrl: action.userIconUrl
                                  };
                                  return (
                                      <Persona
                                          className={className}
                                          identity={identity}
                                          size={PersonaSize.size24}
                                          personaProvider={this.personaProvider}
                                      />
                                  );
                              }
                          }
                        : undefined
                }
                line1={<span className="text-ellipsis">{action.userName}</span>}
                line2={<Ago date={action.date} format={AgoFormat.Compact} />}
            />
        );
    };
}
